import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Breadcrumb } from "../Breadcrumb"
import { Model } from "../../../models/model"
import moment from "moment"
import "./estadoResultado.css"
import { ProfitWaterfall, ResultsChart } from "./ResultCharts"

export const EstadoResultado = () => {
    const { currencyList } = useSelector(state => state.general)
    const [period, setPeriod] = useState('3')
    const [refresh, setRefresh] = useState(0)
    const [report, setReport] = useState(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            const result = await Model.getAll({ url: `reports/eerr?period=${period}` })
            if (active) setReport({ period, refresh, result })
        }
        load()
        return () => { active = false }
    }, [period, refresh])

    const loaded = report?.period === period && report?.refresh === refresh
    const result = loaded ? report.result : null
    const currency = currencyList.find(item => Number(item.id) === result?.currencyId)
    const months = result?.months || []
    const rows = result?.data || []
    const netIncome = rows.find(row => row.flow === 'utilidad')
    let largestChange = null

    months.forEach((_, index) => {
        if (!index || !netIncome) return
        const difference = netIncome.values[index].total - netIncome.values[index - 1].total
        if (difference && (!largestChange || Math.abs(difference) > Math.abs(largestChange.difference))) {
            largestChange = { index, difference }
        }
    })

    const formatAmount = amount => {
        const [integer, decimals] = Math.abs(amount).toFixed(2).split('.')
        const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, () => currency?.separacion_miles || ',')
        return `${amount < 0 ? '−' : ''}${currency?.simbolo || (result?.currencyId === 3 ? 'S/' : '')} ${grouped}${currency?.separacion_decimales || '.'}${decimals}`
    }
    const monthLabel = month => moment(month).locale('es').format('MMM YYYY')

    const changeRows = largestChange ? rows.filter(row => row.show && row.flow !== 'utilidad')
        .map(row => {
            const index = largestChange.index
            const difference = row.values[index].total - row.values[index - 1].total
            return { ...row, impact: ['costos', 'gastos'].includes(row.flow) ? -difference : difference }
        }) : []
    const formatImpact = amount => amount > 0 ? '+' + formatAmount(amount) : formatAmount(amount)
    const formatProjectAmount = amount => amount == null ? '—' : formatAmount(Number(amount))

    const columns = []
    months.forEach((month, index) => {
        const quarter = moment(month).quarter()
        const label = period === 'year' ? `T${quarter} ${month.slice(0, 4)}` : monthLabel(month)
        const previous = columns[columns.length - 1]
        if (previous?.label === label) {
            previous.indices.push(index)
        } else {
            columns.push({ label, indices: [index] })
        }
    })

    const sales = rows.find(row => row.flow === 'ventas' && row.tipo === 'title')
    const chartData = sales && netIncome ? columns.map(({ label, indices }) => ({
        label,
        sales: indices.reduce((sum, index) => sum + sales.values[index].total, 0),
        profit: indices.reduce((sum, index) => sum + netIncome.values[index].total, 0),
    })) : []

    return (
        <div className="p-container er-report">
            <div className="row mt-3">
                <div className="col">
                    <Breadcrumb routes={[
                        { id: 1, name: 'Reportes', link: '/reportes', active: false },
                        { id: 2, name: 'Estado de resultados', link: '/reportes/er', active: true },
                    ]} />
                </div>
            </div>

            <section className="er-heading">
                <div>
                    <span className="er-eyebrow">Reportes financieros</span>
                    <h1 className="p-title">Estado de resultados</h1>
                    <p>Ventas por unidad de negocio, costos, gastos y utilidad.</p>
                </div>
                <div className="er-filters d-flex flex-wrap align-items-end gap-2">
                    <div>
                        <label htmlFor="er-period" className="form-label">Período</label>
                        <select id="er-period" className="form-select" value={period}
                            onChange={event => setPeriod(event.target.value)}>
                            <option value="3">Últimos 3 meses</option>
                            <option value="6">Últimos 6 meses</option>
                            <option value="year">Año en curso</option>
                        </select>
                    </div>
                    <button type="button" className="btn p-btn-secondary"
                        disabled={!loaded}
                        onClick={() => setRefresh(value => value + 1)}>
                        <i className="bi bi-arrow-clockwise me-1" aria-hidden="true" />
                        Actualizar
                    </button>
                </div>

            </section>

            <section aria-label="Detalle del estado de resultados" aria-busy={!loaded}>
                {!loaded && <p role="status">Cargando estado de resultados…</p>}
                {loaded && !result && <p role="alert">No se pudo cargar el reporte. Intenta actualizarlo.</p>}

                {result && !months.length && <p role="status">Aún no hay meses completos en el año en curso.</p>}

                {result && months.length > 0 && (
                    <>
                        <p className="er-period"><strong>{monthLabel(months[0])} a {monthLabel(months[months.length - 1])}</strong></p>
                        <p className="er-source">Importes en {currency?.nombre || (result.currencyId === 3 ? 'soles' : 'moneda local')}. Ventas según presupuestos aprobados por fecha de asignación.</p>
                        <p className="er-insight">
                            <i className="bi bi-graph-up me-2" aria-hidden="true" />
                            {largestChange ? (
                                <>
                                    Mayor cambio de utilidad entre meses completos:{' '}
                                    <strong>{monthLabel(months[largestChange.index])}</strong>{' '}
                                    {largestChange.difference > 0 ? 'subió' : 'bajó'}{' '}
                                    <strong>{formatAmount(Math.abs(largestChange.difference))}</strong>{' '}
                                    respecto de {monthLabel(months[largestChange.index - 1])}.
                                </>
                            ) : 'Sin cambios de utilidad entre meses completos.'}
                        </p>
                        {chartData.length > 0 && <ResultsChart data={chartData} formatAmount={formatAmount} />}
                        <div className="table-responsive er-table-wrap" tabIndex={0} role="region" aria-label="Resultados por período">
                            <table className="table er-table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">Concepto</th>
                                        {columns.map(({ label, indices }) => (
                                            <th scope="col" key={label}
                                                className={`text-end text-nowrap ${indices.includes(largestChange?.index) ? 'er-highlight' : ''}`}>
                                                {label}
                                            </th>
                                        ))}
                                        <th scope="col" className="text-end">Total del período</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.filter(row => row.show).map(row => (
                                        <tr key={row.id} className={row.flow === 'utilidad' ? 'er-result' : row.tipo === 'title' ? 'er-section' : 'er-detail'}>
                                            <th scope="row" className={row.tipo === 'item' ? 'ps-4 fw-normal' : ''}>
                                                {row.referencia}
                                            </th>
                                            {columns.map(({ label, indices }) => {
                                                const total = indices.reduce((sum, index) => sum + row.values[index].total, 0)
                                                const isResult = row.flow === 'utilidad'
                                                const highlight = isResult && indices.includes(largestChange?.index)
                                                return (
                                                    <td key={label} className={`text-end text-nowrap ${isResult && total < 0 ? 'text-danger' : ''} ${highlight ? 'er-highlight' : ''}`}>
                                                        {formatAmount(total)}
                                                    </td>
                                                )
                                            })}
                                            <td className={`text-end text-nowrap er-total ${row.flow === 'utilidad' && row.values[months.length].total < 0 ? 'text-danger' : ''}`}>
                                                {formatAmount(row.values[months.length].total)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {largestChange && (
                            <details className="er-heading d-block">
                                <summary className="fw-bold">Ver qué explica el cambio de utilidad</summary>
                                <p className="mt-3 mb-2">
                                    Impacto en la utilidad de {monthLabel(months[largestChange.index])} frente a {monthLabel(months[largestChange.index - 1])}.
                                </p>
                                <ProfitWaterfall
                                    previous={netIncome.values[largestChange.index - 1].total}
                                    current={netIncome.values[largestChange.index].total}
                                    changes={changeRows.filter(row => row.flow !== 'ventas' || row.tipo === 'item')}
                                    formatAmount={formatAmount}
                                />
                                <div className="table-responsive er-table-wrap">
                                    <table className="table er-table mb-0">
                                        <thead><tr><th scope="col">Concepto</th><th scope="col" className="text-end">Impacto</th></tr></thead>
                                        <tbody>
                                            {changeRows.map(row => (
                                                <tr key={row.id}>
                                                    <th scope="row" className={row.tipo === 'item' ? 'ps-4 fw-normal' : ''}>{row.referencia}</th>
                                                    <td className="text-end text-nowrap">{formatImpact(row.impact)}</td>
                                                </tr>
                                            ))}
                                            <tr className="er-result">
                                                <th scope="row">Variación de utilidad neta</th>
                                                <td className="text-end text-nowrap">{formatImpact(largestChange.difference)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <h2 className="h6 mt-4">Rentabilidad de proyectos</h2>
                                <p className="mb-2">
                                    Proyectos visibles asignados en los dos meses comparados, ordenados por menor margen previsto.
                                    Los costos son acumulados por proyecto.
                                </p>
                                {result.projects?.length ? (
                                    <div className="table-responsive er-table-wrap">
                                        <table className="table er-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Proyecto</th>
                                                    <th scope="col">Mes de asignación</th>
                                                    <th scope="col" className="text-end">Honorarios aprobados</th>
                                                    <th scope="col" className="text-end">Costo directo acumulado</th>
                                                    <th scope="col" className="text-end">Costo pendiente estimado</th>
                                                    <th scope="col" className="text-end">Margen previsto al cierre</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.projects.map(project => (
                                                    <tr key={project.id}>
                                                        <th scope="row">{project.referencia}</th>
                                                        <td className="text-nowrap">{monthLabel(project.month)}</td>
                                                        <td className="text-end text-nowrap">{formatProjectAmount(project.sales)}</td>
                                                        <td className="text-end text-nowrap">{formatProjectAmount(project.cost)}</td>
                                                        <td className="text-end text-nowrap">{project.remaining == null ? 'Pendiente de estimar' : formatProjectAmount(project.remaining)}</td>
                                                        <td className="text-end text-nowrap">
                                                            {project.margin == null ? '—' : `${Number(project.margin).toFixed(1)} %`}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : <p>No hay proyectos visibles para los meses comparados.</p>}
                            </details>
                        )}
                    </>
                )}
            </section>
        </div>
    )
}
